export interface ProjectListItem {
  uid: string;
  name: string;
  description: string;
  categoryId: number;
  categoryName: string;
  riskLevelId: number;
  riskLevelName: string;
  startDate: string;
  endDate: string | null;
  generalBudget: number;
  isActive: boolean;
  investorsCount: number;
  investedCapital: number;
  documentsCount: number;
  notes: string;
}

export interface ProjectListSummary {
  count: number;
  activeCount: number;
  totalBudget: number;
  investedCapital: number;
}

export interface ProjectListFilter {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  categoryIds?: number[];
  riskLevelIds?: number[];
  active?: boolean | null;
}

export interface ProjectListResult {
  items: ProjectListItem[];
  total: number;
  summary: ProjectListSummary;
}

export const EMPTY_PROJECT_SUMMARY: ProjectListSummary = {
  count: 0,
  activeCount: 0,
  totalBudget: 0,
  investedCapital: 0,
};

export interface ProjectRow extends ProjectListItem {
  __expanded?: boolean;
}

export interface ProjectRecord {
  uid?: string;
  name: string;
  description: string;
  categoryId: number;
  riskLevelId: number;
  startDate: string;
  endDate: string | null;
  generalBudget: number;
  isActive: boolean;
  countryCode: string;
  provinceCode: string;
  municipalityCode: string;
  notes: string;
}

export interface ProjectDetailInvestor {
  uid: string;
  name: string;
  nif: string;
  initialCapital: number;
}

export interface ProjectDetailDocument {
  type: string;
  label: string;
  count: number;
  total: number;
}

export interface ProjectDetail {
  uid: string;
  investors: ProjectDetailInvestor[];
  investedCapital: number;
  documents: ProjectDetailDocument[];
}

export interface ProjectInvestment {
  uid?: string;
  projectUid: string;
  name: string;
  nif: string;
  initialCapital: number;
}

export interface ProjectReportExpense {
  name: string;
  total: number;
}

export interface ProjectReportInvestor {
  name: string;
  initialCapital: number;
  share: number;
}

export interface ProjectReport {
  uid: string;
  name: string;
  companyName: string;
  startDate: string;
  endDate: string | null;
  daysElapsed: number;
  daysRemaining: number | null;
  totalDays: number | null;
  delayDays: number;
  generalBudget: number;
  totalExpenses: number;
  totalIncomes: number;
  difference: number;
  differenceBudget: number;
  delayCost: number;
  profitability: number;
  investorsCount: number;
  expensesByType: ProjectReportExpense[];
  investors: ProjectReportInvestor[];
}

export type ProjectDocumentType = 'invoice' | 'ticket';

export interface ProjectExportFile {
  blob: Blob;
  fileName: string;
}

export function toProjectRow(item: ProjectListItem, expanded: boolean): ProjectRow {
  return {
    ...item,
    name: item.name || '',
    description: item.description || '',
    categoryName: item.categoryName || '',
    riskLevelName: item.riskLevelName || '',
    generalBudget: Number(item.generalBudget) || 0,
    investorsCount: Number(item.investorsCount) || 0,
    investedCapital: Number(item.investedCapital) || 0,
    documentsCount: Number(item.documentsCount) || 0,
    isActive: !!item.isActive,
    __expanded: expanded,
  };
}

export function roundMoney(value: number): number {
  return Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;
}

export function formatProjectDate(value: any): string {
  if (!value) return '';
  const text = String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = new Date(text);
  if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '';
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function toIsoDate(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function fromIsoDate(value: any): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.getFullYear() <= 1 ? null : date;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}
